import { Observation, Quantity } from '@medplum/fhirtypes'
import { formatCodeableConcept } from '@medplum/core'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material'
import { format } from 'date-fns'

function formatQuantityWithoutUnit(
    quantity: Quantity | undefined,
    precision?: number
): string {
    if (!quantity) {
        return ''
    }

    const result = []

    if (quantity.comparator) {
        result.push(quantity.comparator)
        result.push(' ')
    }

    if (quantity.value !== undefined) {
        if (precision !== undefined) {
            result.push(quantity.value.toFixed(precision))
        } else {
            result.push(quantity.value)
        }
    }

    return result.join('').trim()
}

function ensureString(value: any): string {
    return value?.toString() || ''
}

function formatObservationValueWithoutUnit(
    obs: Observation | undefined
): string {
    if (!obs) {
        return ''
    }

    const result = []

    if (obs.valueQuantity) {
        result.push(formatQuantityWithoutUnit(obs.valueQuantity))
    } else if (obs.valueCodeableConcept) {
        result.push(formatCodeableConcept(obs.valueCodeableConcept))
    } else {
        const valueString = ensureString(obs.valueString)
        if (valueString) {
            result.push(valueString)
        }
    }

    return result.join(' / ').trim()
}

function formatInterpretation(observation: Observation) {
    if (
        !observation.interpretation ||
        observation.interpretation.length === 0
    ) {
        return 'N/A'
    }
    return formatCodeableConcept(observation.interpretation[0])
}

function observationHasAnyValue(observation: Observation) {
    return (
        observation.valueQuantity ||
        observation.valueCodeableConcept ||
        observation.valueString ||
        observation.valueBoolean ||
        observation.valueInteger ||
        observation.valueRange ||
        observation.valueRatio ||
        observation.valueSampledData ||
        observation.valueTime ||
        observation.valueDateTime ||
        observation.valuePeriod
    )
}

function getDisplayResult(observation: Observation) {
    const obsHasValue = observationHasAnyValue(observation)
    if (!obsHasValue) {
        return formatInterpretation(observation)
    }

    const testResultValue = formatObservationValueWithoutUnit(observation)

    // We have to show the interpration like tiara - e.g "DETECTED A" - we also have to show on lab result list
    // We are commenting for now to avoid inconsistency in the UI
    // const interpretationCode = observation.interpretation?.[0].coding?.[0].code
    // const interpretationPart =
    //     interpretationCode && interpretationCode !== 'N'
    //         ? ` ${interpretationCode}`
    //         : ''

    return testResultValue
}

interface LabResultHistoryTableProps {
    testResultHistory: Observation[]
}

export const LabResultHistoryTable = ({
    testResultHistory
}: LabResultHistoryTableProps) => {
    return (
        <TableContainer
            component={Paper}
            sx={{
                my: 3,
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }}
        >
            <Table aria-label="lab result history table">
                <TableHead>
                    <TableRow
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <TableCell
                            sx={{
                                py: 2,
                                px: 3,
                                color: 'text.secondary',
                                borderBottom: 'none'
                            }}
                        >
                            Date
                        </TableCell>
                        <TableCell
                            sx={{
                                py: 2,
                                px: 3,
                                color: 'text.secondary',
                                borderBottom: 'none'
                            }}
                        >
                            Value
                        </TableCell>
                        <TableCell
                            sx={{
                                py: 2,
                                px: 3,
                                color: 'text.secondary',
                                borderBottom: 'none'
                            }}
                        >
                            Normal Range
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {testResultHistory.map((result, index) => {
                        const effectiveDate = result.effectiveDateTime
                            ? new Date(result.effectiveDateTime)
                            : null

                        const displayValue = getDisplayResult(result)

                        const referenceText = result.referenceRange?.[0]?.text

                        return (
                            <TableRow
                                key={index}
                                sx={{
                                    'borderBottom':
                                        index === testResultHistory.length - 1
                                            ? 'none'
                                            : '1px solid',
                                    'borderColor': 'divider',
                                    '&:last-child td': { borderBottom: 0 }
                                }}
                            >
                                <TableCell
                                    sx={{
                                        py: 2,
                                        px: 3,
                                        color: 'text.secondary',
                                        borderBottom: 'none'
                                    }}
                                >
                                    {effectiveDate
                                        ? format(effectiveDate, 'MMMM d, yyyy')
                                        : 'N/A'}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        py: 2,
                                        px: 3,
                                        color: 'text.secondary',
                                        borderBottom: 'none'
                                    }}
                                >
                                    {displayValue || 'N/A'}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        py: 2,
                                        px: 3,
                                        color: 'text.secondary',
                                        borderBottom: 'none'
                                    }}
                                >
                                    {referenceText || 'N/A'}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
